import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../task-list/TaskList.css';
import profileIcon from '../../../shared/assets/icons/profile.png';
import bellIcon from '../../../shared/assets/icons/bell.png';
import backIcon from '../../../shared/assets/icons/back.png';
import addIcon from '../../../shared/assets/icons/add.png';
import actionIcon from '../../../shared/assets/icons/eye.png';
import DataTableGrid from '../../../shared/components/DataTableGrid';
import { taskDetailsService } from '../services/taskDetailsService';
import HeaderNav from '../../../shared/components/HeaderNav';
import SearchInput from '../../../components/SearchInput';

const MaintenanceList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const equipmentId = searchParams.get('equipment_id');
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchMaintenanceRequests = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await taskDetailsService.fetchMaintenanceRequests(currentPage, itemsPerPage, sortField, sortOrder, equipmentId, searchQuery);
        if (data && data.maintenanceRequests) {
          setMaintenanceRequests(data.maintenanceRequests);
          setTotalItems(data.totalItems || data.maintenanceRequests.length);
        } else {
          setMaintenanceRequests([]);
          setTotalItems(0);
        }
      } catch (error) {
        setError(error.message);
        setMaintenanceRequests([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };
    fetchMaintenanceRequests();
  }, [currentPage, itemsPerPage, sortField, sortOrder, equipmentId, searchQuery]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const searchTimerRef = React.useRef(null);
  const handleSearchChange = (e) => {
    const val = e.target.value;
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchQuery(val);
      setCurrentPage(1);
    }, 500);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="task-list-container">
      {/*Greeting Header */}
      <HeaderNav
        profileIcon={profileIcon}
      />

      <div className="header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
        <button className="back-button">
          <img src={backIcon} alt="Back" className="icon back-icon" onClick={() => navigate('/dashboard')} />
        </button>
        <h2 className="page-title" style={{ fontSize: '20px', fontFamily: 'Montserrat' }}>Maintenance Requests</h2>
        <div className="filters" style={{ marginLeft: 'auto' }}>
          <button className="add-button" onClick={() => navigate('/maintenance')} style={{ height: '37px', width: '70px', marginBottom: '5px', marginRight: '25px', borderRadius: '5px' }}>
            <img src={addIcon} alt="Add" className="icon" />
          </button>
        </div>
      </div>

      {/* Mobile Header */}
      <header className="mobile-header" style={{ backgroundColor: '#202941' }}>
        <div className="back-button" onClick={() => navigate('/dashboard')}>
          <img src={backIcon} alt="Back" className="icon back-icon" />
        </div>
        <div className="main-mobile-header">
          <h2 className="main-title" style={{ fontSize: '20px', fontFamily: 'Montserrat' }}>Maintenance Requests</h2>
        </div>
        <div className="header-icons">
          <img src={bellIcon} alt="Notifications" className='notification-icon' />
        </div>
      </header>

      {/* Mobile Page Title */}
      <div className="mobile-page-title">
        <h2>Maintenance Requests</h2>
      </div>

      {/* Mobile Search Bar */}
      <div className="mobile-search-bar">
        <SearchInput
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search requests..."
          width="100%"
        />
      </div>

      <DataTableGrid
        items={maintenanceRequests}
        loading={loading}
        error={error}
        emptyMessage="No maintenance requests found"
        onSort={handleSort}
        sortField={sortField}
        getSortIcon={getSortIcon}
        columns={[
          { key: 'id', title: 'Request ID', sortKey: 'id' },
          { key: 'equipment_name', title: 'Equipment', sortKey: 'equipment_name' },
          { key: 'issue_type', title: 'Issue Type', sortKey: 'issue_type' },
          {
            key: 'priority',
            title: 'Priority',
            sortKey: 'priority',
            render: (request) => (
              <span className={`priority ${String(request.priority ?? '').toLowerCase()}`}>{request.priority}</span>
            )
          },
          {
            key: 'status',
            title: 'Status',
            sortKey: 'status',
            render: (request) => (
              <>
                <span className={`status-circle ${String(request.status ?? '').toLowerCase().replace(' ', '-')}`}></span>
                {request.status}
              </>
            )
          },
          { key: 'requested_date', title: 'Date', sortKey: 'requested_date' },
          {
            key: 'actions',
            title: 'Actions',
            render: (request) => (
              <button className="action-button" onClick={() => navigate(`/maintenancedetails/${request.id}`)}>
                <img src={actionIcon} alt="action" className="icon" />
              </button>
            )
          }
        ]}
      />

      {/* Mobile Content */}
      <div className="task-content-container">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#ffffff' }}>
            Loading...
          </div>
        ) : maintenanceRequests.length === 0 ? (
          <div className="mobile-empty-state">
            <div className="mobile-empty-icon">{searchQuery ? '🔍' : '📭'}</div>
            <div className="mobile-empty-text">{searchQuery ? 'No requests match your search' : 'No Maintenance Requests Available'}</div>
          </div>
        ) : (
          maintenanceRequests.map((request, index) => (
            <div key={`mobile-${currentPage}-${request.id}-${index}`} className="task-card">
              <div className="card-header">
                <span className="technician-name">{request.id}</span>
                <span className={`priority-badge ${String(request.priority ?? '').toLowerCase()}`}>
                  {request.priority}
                </span>
              </div>
              <div className="priority-date-row">
                <span className="status-label">
                  <span className={`status-circle ${String(request.status ?? '').toLowerCase().replace(' ', '-')}`}></span>
                  {request.status}
                </span>
                <span className="schedule-date">{request.requested_date}</span>
              </div>
              <div className="equipment-description">
                <strong>{request.equipment_name}</strong> - {request.issue_type}
                <br />
                {request.description}
              </div>
              <div className="card-actions">
                <button className="action-button" onClick={() => navigate(`/maintenancedetails/${request.id}`)}>
                  <img src={actionIcon} alt="View Details" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Pagination */}
      {!isMobile && totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-button"
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || loading}
            style={{ opacity: currentPage === 1 || loading ? 0.5 : 1 }}
          >
            {"<"}
          </button>
          {Array.from({ length: Math.min(totalPages, 10) }, (_, index) => {
            let pageNumber;
            if (totalPages <= 10) {
              pageNumber = index + 1;
            } else {
              const start = Math.max(1, currentPage - 5);
              const end = Math.min(totalPages, start + 9);
              pageNumber = start + index;
              if (pageNumber > end) return null;
            }
            return (
              <button
                key={pageNumber}
                className={`page-number-button ${currentPage === pageNumber ? 'active' : ''}`}
                onClick={() => handlePageClick(pageNumber)}
                disabled={loading}
              >
                {pageNumber}
              </button>
            );
          }).filter(Boolean)}
          <button
            className="page-button"
            onClick={handleNextPage}
            disabled={currentPage === totalPages || loading}
            style={{ opacity: currentPage === totalPages || loading ? 0.5 : 1 }}
          >
            {">"}
          </button>
        </div>
      )}

      {/* Mobile Pagination */}
      {isMobile && totalPages > 1 && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#1A2134',
          padding: '15px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          borderTop: '1px solid #444'
        }}>
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || loading}
            style={{
              backgroundColor: '#202941',
              color: '#ffffff',
              border: '1px solid #444',
              borderRadius: '4px',
              padding: '8px 12px',
              cursor: currentPage === 1 || loading ? 'not-allowed' : 'pointer',
              opacity: currentPage === 1 || loading ? 0.5 : 1
            }}
          >
            Previous
          </button>
          <span style={{ color: '#ffffff', fontSize: '14px' }}>
            {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages || loading}
            style={{
              backgroundColor: '#202941',
              color: '#ffffff',
              border: '1px solid #444',
              borderRadius: '4px',
              padding: '8px 12px',
              cursor: currentPage === totalPages || loading ? 'not-allowed' : 'pointer',
              opacity: currentPage === totalPages || loading ? 0.5 : 1
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default MaintenanceList;